import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LLMAssertionConfiguration, ReportColumn } from "@/types/evaluate";
import { formatInputVariable } from "@/utils/evaluate";
import { useMemo, useState } from "react";
import { useBandaid } from "../../utils/BandaidContext";
import { ModalStep } from "../ModalRouter";
import SourceName from "../SourceName";
import { MultipleAssertions } from "./MultipleAssertions";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { Info } from "lucide-react";
const LLMAssertionBuilder = ({
  columnData,
  availableColumns,
  navigatePrevious,
  saveColumnAction,
  editable,
}: {
  columnData: Partial<ReportColumn>;
  availableColumns: ReportColumn[];
  navigatePrevious: () => void;
  saveColumnAction: (newColumnDataArg: ReportColumn) => void;
  editable: boolean;
}) => {
  const [name, setName] = useState<string | null>(columnData.name || null);
  const [prompt, setPrompt] = useState<string>(
    columnData.configuration?.prompt ?? "",
  );

  const [promptColumn, setPromptColumn] = useState<string | null>(
    columnData.configuration?.prompt_source ?? null,
  );
  const [usePromptColumn, setUsePromptColumn] = useState<boolean>(
    !!columnData.configuration?.prompt_source,
  );
  const [column, setColumn] = useState<string | null>(
    columnData.configuration?.source || null,
  );

  const bandaid = useBandaid();

  const [dataIsValid, setDataIsValid] = useState<{
    name: boolean;
    prompt: boolean;
    column: boolean;
  }>({
    name: true,
    prompt: true,
    column: true,
  });

  const saveEndpointAction = () => {
    if (!name) {
      setDataIsValid({ name: false, prompt: true, column: true });
      return;
    } else if (!column) {
      setDataIsValid({ name: true, prompt: true, column: false });
      return;
    }

    setDataIsValid({ name: true, prompt: true, column: true });

    saveColumnAction({
      ...columnData,
      name: name,
      configuration: {
        prompt: usePromptColumn ? undefined : prompt,
        source: column,
        prompt_source: usePromptColumn ? promptColumn : undefined,
      } as LLMAssertionConfiguration,
    } as ReportColumn);
  };

  const assertions = useMemo(() => {
    if (!prompt) {
      return [];
    }

    try {
      return prompt.startsWith("[") ? JSON.parse(prompt) : [prompt];
    } catch {
      return [prompt];
    }
  }, [prompt]);

  return (
    <ModalStep
      navigatePrevious={navigatePrevious}
      navigateNext={saveEndpointAction}
      nextButtonText={editable ? "Save Step" : "Done"}
    >
      <div className="grid grid-cols-3 items-center gap-4">
        <div className="col-span-3">
          <div className="text-lg font-semibold">LLM Assertion</div>
          <div className="max-w-md text-sm text-gray-500">
            This step will use an LLM to check an assertion and return
            true/false.
          </div>
        </div>
        <SourceName
          columnCount={availableColumns.length}
          name={name!}
          isValid={dataIsValid.name}
          setName={setName}
          isNode={bandaid}
          readonly={!editable}
        />
        {usePromptColumn ? (
          <>
            <label htmlFor="prompt-column" className="col-span-1">
              Prompt {bandaid ? "Node" : "Column"}:
            </label>
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={!editable}
                className="col-span-2 w-full"
              >
                {promptColumn || (
                  <span className="font-normal text-gray-500">
                    Select a {bandaid ? "node" : "column"}...
                  </span>
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {availableColumns.map((column) => (
                  <DropdownMenuItem
                    key={column.name}
                    onSelect={() => setPromptColumn(column.name)}
                  >
                    {formatInputVariable(column.column_type, column.name)}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <>
            <label htmlFor="prompt" className="col-span-1">
              Assertion:
            </label>
            <MultipleAssertions
              assertions={assertions}
              onAssertionsChange={(assertions) =>
                setPrompt(JSON.stringify(assertions))
              }
            />
          </>
        )}

        <div className="col-span-1">&nbsp;</div>
        <div className="col-span-2 flex items-center gap-2">
          <input
            type="checkbox"
            id="use-prompt-column"
            checked={usePromptColumn}
            onChange={(e) => setUsePromptColumn(e.target.checked)}
            disabled={!editable}
            className="cursor-pointer"
          />
          <label htmlFor="use-prompt-column" className="cursor-pointer text-sm">
            Use {bandaid ? "node" : "column"} variable for assertion
          </label>
          <BasicTooltip
            content={`You can use a ${
              bandaid ? "node" : "column"
            } variable for the assertion. This will use the value of the ${
              bandaid ? "node" : "column"
            } as the assertion.`}
          >
            <Info className="h-4 w-4 cursor-pointer hover:text-gray-500" />
          </BasicTooltip>
        </div>

        <label htmlFor="column" className="col-span-1">
          {bandaid ? "Node" : "Column"}:
        </label>
        <DropdownMenu>
          <DropdownMenuTrigger
            disabled={!editable}
            className="col-span-2 w-full"
          >
            {column || (
              <span className="font-normal text-gray-500">
                Select a {bandaid ? "node" : "column"}...
              </span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {availableColumns.map((column) => (
              <DropdownMenuItem
                key={column.name}
                onSelect={() => setColumn(column.name)}
              >
                {formatInputVariable(column.column_type, column.name)}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {!dataIsValid.column && (
          <>
            <div className="col-span-1 -mt-4">&nbsp;</div>
            <div className="col-span-2 -mt-4 text-left text-xs text-red-500">
              {bandaid ? "Node" : "Column"} is required for this step
            </div>
          </>
        )}
      </div>
    </ModalStep>
  );
};

export default LLMAssertionBuilder;
